import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api/axios";
import { AUTH_GRADIENT_STYLE } from "../../constants/authVisuals";
import AppModalShell from "../common/AppModalShell";
import CapacityTierSlider from "./CapacityTierSlider";
import EventCreateStep1 from "./EventCreateStep1";
import { resolveCapacityUpgradePrompt, resolveEventCapacityState } from "./capacityTierUtils";
import {
  buildQuickConceptPayload,
  getQuickConceptBadges,
  matchQuickConceptCity,
  suggestQuickConceptCategory,
} from "./quickConcept";

const AGE_OPTIONS = [
  { value: "all", min: "", max: "" },
  { value: "18_24", min: 18, max: 24 },
  { value: "25_34", min: 25, max: 34 },
  { value: "35_44", min: 35, max: 44 },
  { value: "45_plus", min: 45, max: "" },
];
const emptyDraft = () => ({ title: "", description: "", age: "all", badgeId: "", maxParticipants: "15" });
const FIELD_CLASS = "h-[50px] w-full rounded-[41px] border border-white/25 bg-black/10 px-[25px] text-[15px] text-white outline-none focus:border-white/45";
const LABEL_CLASS = "mb-[6px] block text-[24px] font-semibold leading-[1.15] text-white";
const FINAL_MODERATION_STATES = new Set(["approved", "manual_review", "changes_requested", "rejected"]);

const ConceptQuickComposer = ({
  open, onClose, onCreated, onAdvanced, selectedCity, selectedCountry, selectedMunicipalityId,
}) => {
  const { t } = useTranslation("events");
  const [draft, setDraft] = useState(emptyDraft);
  const [city, setCity] = useState(null);
  const [badges, setBadges] = useState([]);
  const [mapMarker, setMapMarker] = useState(null);
  const [hostRating, setHostRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [createdEventId, setCreatedEventId] = useState(null);
  const [moderationStatus, setModerationStatus] = useState("processing");
  const [expanded, setExpanded] = useState(false);
  const [expandedDraft, setExpandedDraft] = useState(null);

  const category = useMemo(
    () => suggestQuickConceptCategory(`${draft.title} ${draft.description}`),
    [draft.title, draft.description]
  );
  const badgeOptions = useMemo(() => getQuickConceptBadges(badges, category), [badges, category]);
  const selectedBadge = badgeOptions.find((badge) => badge.id === draft.badgeId) || badgeOptions[0] || null;
  const selectedAge = AGE_OPTIONS.find((option) => option.value === draft.age) || AGE_OPTIONS[0];
  const capacityState = useMemo(() => resolveEventCapacityState({
    hostRating,
    formValue: draft.maxParticipants,
    pricingModel: "free",
  }), [hostRating, draft.maxParticipants]);
  const capacityValue = capacityState.normalizedMaxParticipants;
  const capacityUpgradePrompt = resolveCapacityUpgradePrompt(capacityState, capacityValue);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      api.get("/accounts/profile/"),
      api.get("/miniapp/get-skins/?category=event_badge"),
      api.get("/miniapp/get-skins/?category=map_marker"),
      api.get("/events/get-host-rating/").catch(() => ({ data: null })),
    ]).then(async ([profileResult, badgeResult, markerResult, ratingResult]) => {
      const profile = profileResult.data || {};
      const preferredCity = selectedCity || profile.city || "";
      const preferredCountry = selectedCountry || profile.country || "";
      let matchedCity = null;
      if (preferredCity) {
        const { data } = await api.get("/accounts/locations/cities/", {
          params: { country: preferredCountry, q: preferredCity },
        });
        matchedCity = matchQuickConceptCity(
          data?.cities, preferredCity, selectedMunicipalityId || profile.municipality_id
        );
      }
      if (!active) return;
      const markerOptions = Array.isArray(markerResult.data) ? markerResult.data : [];
      setBadges(Array.isArray(badgeResult.data) ? badgeResult.data : []);
      setMapMarker(markerOptions.find((item) => item?.is_default && /bubble[-_]blue/i.test(item.name || ""))
        || markerOptions.find((item) => item?.is_default) || null);
      setHostRating(ratingResult.data || null);
      setCity(matchedCity);
    }).catch(() => {
      if (active) setError(t("quick_concept.load_error"));
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [open, selectedCity, selectedCountry, selectedMunicipalityId, t]);

  useEffect(() => {
    if (!open || !createdEventId || FINAL_MODERATION_STATES.has(moderationStatus)) return undefined;
    let active = true;
    const checkStatus = async () => {
      try {
        const { data } = await api.get(`/events/${createdEventId}/moderation-status/`);
        if (active) setModerationStatus(FINAL_MODERATION_STATES.has(data?.status) ? data.status : "processing");
      } catch {
        // Creation succeeded; status can still be processing when the check is unavailable.
      }
    };
    void checkStatus();
    const timer = window.setInterval(checkStatus, 3000);
    return () => { active = false; window.clearInterval(timer); };
  }, [createdEventId, moderationStatus, open]);

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    if (error) setError("");
  };
  const handleClose = () => {
    if (submitting) return;
    document.activeElement?.blur?.();
    onClose?.();
  };
  const handleExitComplete = () => {
    setDraft(emptyDraft());
    setExpanded(false);
    setExpandedDraft(null);
    setCreatedEventId(null);
    setModerationStatus("processing");
    setError("");
  };
  const advancedDraft = {
    eventName: draft.title.trim(),
    description: draft.description.trim(),
    category,
    sportType: "",
    targetGender: "",
    targetAgeMin: selectedAge.min,
    targetAgeMax: selectedAge.max,
    maxParticipants: capacityValue,
    eventFormat: 1,
    hostFormat: 0,
    pricingModel: "free",
    joinByRequest: false,
    audienceMode: "open",
    badgeImage: selectedBadge?.src || "",
    badgeId: selectedBadge?.dbId || "",
    mapLabelId: mapMarker?.id || "",
    latitude: city?.latitude,
    longitude: city?.longitude,
    city: city?.name || "",
    country: city?.country || "",
    publicationMunicipalityId: city?.municipality_id ? String(city.municipality_id) : "",
    publicationCity: city?.name || "",
    publicationCityWasChosen: false,
  };
  const handleAdvanced = () => {
    if (submitting || expanded) return;
    document.activeElement?.blur?.();
    setExpandedDraft((current) => current ? {
      ...current,
      ...advancedDraft,
      category: current.category,
      sportType: current.sportType,
    } : advancedDraft);
    setExpanded(true);
  };
  const handleExpandedChange = (field, value) => {
    setExpandedDraft((current) => current ? { ...current, [field]: value } : current);
  };
  const handleExpandedBack = () => {
    setDraft((current) => ({
      ...current,
      title: expandedDraft?.eventName || "",
      description: expandedDraft?.description || "",
    }));
    setExpanded(false);
  };

  const submit = async () => {
    if (submitting) return;
    if (!draft.title.trim() || !draft.description.trim()) {
      setError(t("quick_concept.required_error"));
      return;
    }
    if (capacityUpgradePrompt || !Number.isInteger(capacityValue) || capacityValue < 3) {
      setError(t("quick_concept.capacity_error"));
      return;
    }
    if (!city || city.latitude == null || city.longitude == null) {
      setError(t("quick_concept.city_error"));
      return;
    }
    if (!selectedBadge?.dbId) {
      setError(t("quick_concept.badge_error"));
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const payload = buildQuickConceptPayload({
        title: draft.title, description: draft.description, city,
        badge: selectedBadge, mapMarker, age: selectedAge,
        maxParticipants: capacityValue,
      });
      const { data } = await api.post("/events/create/", payload, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!data?.event_id) throw new Error(t("quick_concept.submit_error"));
      setModerationStatus("processing");
      setCreatedEventId(data.event_id);
      onCreated?.(data);
    } catch (submitError) {
      setError(submitError?.response?.data?.error || submitError.message || t("quick_concept.submit_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const formData = {
    eventName: draft.title, description: draft.description, category,
    sportType: category === "sport" ? "generic" : "",
  };
  const extraFields = (
    <>
      <div>
        <label htmlFor="quick-concept-age" className={LABEL_CLASS}>{t("quick_concept.age")}</label>
        <div className="relative">
          <select id="quick-concept-age" className={`${FIELD_CLASS} appearance-none pr-12`} value={draft.age} onChange={(event) => update("age", event.target.value)}>
            {AGE_OPTIONS.map((option) => <option key={option.value} value={option.value} className="text-black">{t(`quick_concept.age_options.${option.value}`)}</option>)}
          </select>
          <svg aria-hidden="true" viewBox="0 0 24 24" className="pointer-events-none absolute right-[19px] top-1/2 h-5 w-5 -translate-y-1/2 text-white/70" fill="none">
            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      <div>
        <p className={LABEL_CLASS}>{t("quick_concept.capacity")}</p>
        <CapacityTierSlider
          capacityState={capacityState}
          value={capacityValue}
          onChange={(value) => update("maxParticipants", String(value))}
          translationKeyPrefix="event_create.step3.capacity"
          sliderAriaKey="event_create.step3.capacity.slider_aria"
          variant="additional"
          showIncludesHostHint
          disabled={submitting}
        />
      </div>
      <div>
        <p className={LABEL_CLASS}>{t("quick_concept.icon")}</p>
        <div className="flex gap-2 overflow-x-auto pb-2">
          {badgeOptions.map((badge) => (
            <button key={badge.id} type="button" onClick={() => update("badgeId", badge.id)} aria-label={badge.id.replace(/[-_]/g, " ")} aria-pressed={selectedBadge?.id === badge.id}
              className={`h-[62px] w-[62px] shrink-0 overflow-hidden rounded-[12px] border-2 bg-white ${selectedBadge?.id === badge.id ? "border-[#8fb1ff]" : "border-transparent"}`}>
              <img src={badge.src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      {error ? <p role="alert" className="text-sm font-medium text-[#ffb0b7]">{error}</p> : null}
    </>
  );

  const statusKey = FINAL_MODERATION_STATES.has(moderationStatus) ? moderationStatus : "processing";
  const quickConceptActions = (
    <div className="flex items-center justify-center gap-3">
      <button type="button" onClick={handleAdvanced} className="min-h-[55px] flex-1 rounded-full border border-white/25 bg-white/10 px-3 text-[14px] font-semibold text-white">
        {t("quick_concept.advanced")}
      </button>
      <button type="button" onClick={submit} disabled={loading || submitting || Boolean(capacityUpgradePrompt) || !draft.title.trim() || !draft.description.trim()}
        className="min-h-[55px] flex-1 rounded-full border border-white/20 bg-[#3e6ffb] px-3 text-[14px] font-semibold text-white disabled:opacity-45">
        {submitting ? t("quick_concept.sending") : t("quick_concept.create")}
      </button>
    </div>
  );
  return (
    <AppModalShell
      open={open}
      onExitComplete={handleExitComplete}
      title={createdEventId
        ? t(`event_create.step4.post_create_modal.states.${statusKey}.title_concept`)
        : t("quick_concept.author")}
      onClose={handleClose}
      hideHeader
      mobileSheet
      mobileSheetTopInset={expanded ? "0px" : undefined}
      swipeToClose={!expanded}
      preserveMobileSheetHeight
      layout
      closeAriaLabel={t("quick_concept.close")}
      maxWidthClassName={expanded ? "max-w-none lg:max-w-[520px]" : "max-w-none lg:max-w-[402px]"}
      desktopMaxHeight="min(90vh, 820px)"
      panelClassName={`${expanded ? "!rounded-t-none" : "!rounded-t-[26px]"} !rounded-b-none !border-x-0 !border-b-0 !backdrop-blur-none lg:!rounded-[26px] lg:!border`}
      panelStyle={AUTH_GRADIENT_STYLE}
      bodyClassName="!px-0 !pb-0 !pt-0"
      footer={createdEventId ? (
        <button type="button" onClick={handleClose} className="h-[55px] w-full rounded-full bg-[#3e6ffb] text-[15px] font-semibold text-white">
          {t("quick_concept.done")}
        </button>
      ) : null}
    >
      {createdEventId ? (
        <div className="mx-auto flex min-h-[420px] max-w-[402px] flex-col items-center justify-center px-[26px] text-center text-white">
          <h2 className="text-[28px] font-semibold leading-tight">{t(`event_create.step4.post_create_modal.states.${statusKey}.title_concept`)}</h2>
          <p className="mt-5 text-[17px] font-semibold">{t(`event_create.step4.post_create_modal.states.${statusKey}.status_concept`)}</p>
          <p className="mt-3 text-[14px] leading-6 text-white/75">{t(`event_create.step4.post_create_modal.states.${statusKey}.description_concept`)}</p>
        </div>
      ) : (
        <EventCreateStep1
          formData={expanded ? expandedDraft : formData}
          handleChange={(field, value) => {
            if (expanded) handleExpandedChange(field, value);
            else if (field === "eventName") update("title", value);
            else if (field === "description") update("description", value);
          }}
          handleBack={expanded ? handleExpandedBack : handleClose}
          handleNext={expanded ? () => onAdvanced?.(expandedDraft) : handleAdvanced}
          onClose={handleClose}
          mode="create"
          creationType="concept"
          progressWidth={expanded ? "33.333333%" : "16.666667%"}
          quickConceptMode={!expanded}
          morphingFromQuickConcept
          quickConceptExtraFields={extraFields}
          quickConceptFooter={quickConceptActions}
        />
      )}
    </AppModalShell>
  );
};

export default ConceptQuickComposer;
